import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'components/shared';
import {
  StyledInfoWindow,
  StyledSearchButton,
  ValidationMessage,
  StyledFooter,
  StyledTitle,
  AddContactButton,
  StyledHeader,
  ScheduleHeaderWrapper,
} from './styles';
import { useScheduleTable } from '../../../Application/SchedulePage/context';
import ContactsTable from './components/ContactTable';
import UploadDownloadSection from './components/ImportDownloadButtons';
import ScheduleModal from '../ScheduleTable/components/SсheduleModal';
import ScheduleTooltip from '../ScheduleTooltip';

function ScheduleHeader({
  onLlcClicked,
  onStateClicked,
  onDownloadTemplateClicked,
  importApplicationSchedule,
}) {
  const {
    stateName,
    user,
    legalName,
    token,
    setSearchButtonClicked,
    contacts,
    addContact,
    removeContact,
    updateContact,
    isContactValid,
    isSearchDisabled,
    getContactErrors,
  } = useScheduleTable();
  const [showError, setShowError] = React.useState(false);
  const [modalWindowVisible, setModalWindowVisible] = React.useState(false);
  const [uploadButtonClicked, setUploadButtonClicked] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle');

  const handleSearchClick = useCallback(() => {
    setShowError(false);
    if (!isSearchDisabled) {
      setSearchButtonClicked(true);
    } else {
      setShowError(true);
    }
  }, [setSearchButtonClicked, isSearchDisabled]);
  useEffect(() => {
    setShowError(false);
  }, [contacts]);

  const handleUploadTemplate = useCallback((uploadedFile, withReplace) => {
    if (user && user.id && token) {
      setUploadStatus('loading');
      setUploadProgress(10);

      const onProgress = (percentage, status) => {
        setUploadProgress(percentage);
        setUploadStatus(status);
      };

      importApplicationSchedule(token, uploadedFile, withReplace, onProgress)
        .finally(() => {
          setUploadStatus('completed');
        });
    }
  }, [importApplicationSchedule, token, user, setUploadProgress, setUploadStatus]);

  useEffect(() => {
    if (uploadButtonClicked) {
      setModalWindowVisible(true);
      setUploadButtonClicked(false);
    }
  }, [uploadButtonClicked, setUploadButtonClicked]);

  const onAppend = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx,.xls,.csv';
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleUploadTemplate(file, false);
      }

      setModalWindowVisible(false);
    };

    fileInput.click();
  }, [handleUploadTemplate]);

  const onReplace = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.xlsx,.xls,.csv';
    fileInput.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        handleUploadTemplate(file, true);
      }

      setModalWindowVisible(false);
    };

    fileInput.click();
  }, [handleUploadTemplate]);
  const onCancel = () => {
    setModalWindowVisible(false);
    setUploadButtonClicked(false);
  };

  const handleCloseModal = () => {
    setModalWindowVisible(false);
    setUploadButtonClicked(false);
  };

  return (
    <>
      {modalWindowVisible && (
        <ScheduleModal
          onAppend={onAppend}
          onReplace={onReplace}
          onCancel={onCancel}
          onClose={handleCloseModal}
        />
      )}
      <ScheduleHeaderWrapper>
        <StyledInfoWindow>
          <StyledHeader>
            <StyledTitle>Search Your Properties <ScheduleTooltip width={1.3} text="Look up properties by name, state, or LLC" /></StyledTitle>
            <AddContactButton onClick={addContact}>
              + Add Entity
            </AddContactButton>
          </StyledHeader>
          <ContactsTable
            contacts={contacts}
            onInputChange={updateContact}
            onRemoveContact={removeContact}
            onStateClick={onStateClicked}
            onLlcClick={onLlcClicked}
            stateName={stateName}
            legalName={legalName}
            isContactValid={isContactValid}
            getContactErrors={getContactErrors}
            showError={showError}
            setShowError={setShowError}
          />
          <StyledFooter>
            {showError && isSearchDisabled && (
              <ValidationMessage>
                Please fill required fields: each contact must have
                State and either Full Name or LLC Name
              </ValidationMessage>
            )}

            <StyledSearchButton
              onClick={handleSearchClick}
            >
              <Icon icon="searchLense" withoutFill />Search Properties
            </StyledSearchButton>
          </StyledFooter>
        </StyledInfoWindow>
        <UploadDownloadSection
          onDownloadTemplateClicked={onDownloadTemplateClicked}
          setUploadButtonClicked={setUploadButtonClicked}
          uploadProgress={uploadProgress}
          uploadStatus={uploadStatus}
        />
      </ScheduleHeaderWrapper>
    </>
  );
}

ScheduleHeader.propTypes = {
  onLlcClicked: PropTypes.func,
  onStateClicked: PropTypes.func,
  onDownloadTemplateClicked: PropTypes.func,
  importApplicationSchedule: PropTypes.func.isRequired,
};

ScheduleHeader.defaultProps = {
  onLlcClicked: () => { },
  onStateClicked: () => { },
  onDownloadTemplateClicked: () => { },
};

export default ScheduleHeader;
